import type { ReactNode } from 'react';

interface HighlightTextProps {
  /** The full text to render */
  text: string | null | undefined;
  /** The search query — case-insensitive substring match; occurrences are wrapped in <mark> */
  query: string | null | undefined;
  /** Optional className applied to the wrapper span (useful for text-inherit, etc.) */
  className?: string;
}

/**
 * Marks all case-insensitive occurrences of `query` inside `text`.
 * Falls back to plain text when query is empty.
 *
 * The match is shown as a thin underline, not a filled highlight: a row of
 * results is mostly matched text, and blocks of fill turn the list into a
 * field of colored patches that fights the status pills and tag chips for
 * attention. The underline points at the match without repainting the row.
 * (It also drops the old hardcoded `bg-yellow-300 text-black`, which ignored
 * the theme and went unreadable in dark mode.)
 *
 * The rule sits a step BELOW the text in weight — `muted-foreground/50` — so
 * it reads as an annotation on the word rather than part of it. Alpha rather
 * than a lighter token because there isn't one that works in both themes:
 * `border` is #edf2f9 on white and #3a4055 on the dark surface, invisible
 * either way, while a percentage of `muted-foreground` stays proportionally
 * light against both.
 */
export function HighlightText({ text, query, className }: HighlightTextProps) {
  if (!text) return null;
  const q = query?.trim() ?? '';
  if (!q) return <span className={className}>{text}</span>;

  const lowerText = text.toLowerCase();
  const lowerQ = q.toLowerCase();
  const parts: ReactNode[] = [];
  let lastIdx = 0;
  let idx = lowerText.indexOf(lowerQ);
  let key = 0;
  while (idx !== -1) {
    if (idx > lastIdx) parts.push(text.slice(lastIdx, idx));
    parts.push(
      <mark
        key={key++}
        data-testid="highlight-match"
        // `<mark>` ships a yellow UA background and its own color — both have
        // to be overridden explicitly or the browser default bleeds through.
        className="bg-transparent text-inherit underline decoration-muted-foreground/50 decoration-1 underline-offset-2"
      >
        {text.slice(idx, idx + q.length)}
      </mark>,
    );
    lastIdx = idx + q.length;
    idx = lowerText.indexOf(lowerQ, lastIdx);
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return <span className={className}>{parts}</span>;
}
